/**
 * CartItem.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import React from 'react';
import { useCart } from '../hooks/useCart';
import AsyncProductImage from './AsyncProductImage';
import { getIcon } from '../utils/icons';
import { getLineUnitPriceCents } from '../utils/cartPricing';
import { formatCartMoney, formatDualCurrency, showsReferenceCurrency } from '../utils/currencyUtils';
import { useCurrencyRates } from '../hooks/useCurrencyRates';
import BundleContentsToggle from './BundleContentsToggle';
import { useLanguage } from '../context/LanguageContext';
import { fmt } from '../i18n/format';
import './CartItem.css';

const CartItem = ({ item, customerCountry = 'CA', customerCountryName = '' }) => {
  const { updateQuantity, removeItem } = useCart();
  const currencyRates = useCurrencyRates();
  const { t } = useLanguage();
  const cart = t.shop.cart;

  // Early return if item is not available (after hooks)
  if (!item) {
    return <div className="cart-item">{cart.unavailable}</div>;
  }

  // Check if this is a bundle
  const isBundle = item.isBundle || false;
  const bundleDiscount = item.bundleDiscount || 0;
  const bundleOriginalPrice = item.originalPrice || 0;
  const bundleProducts = item.bundleProducts || [];
  const isCheckoutPromo = item.isCheckoutPromo || false;
  const isDigitalOrVirtual =
    !isCheckoutPromo &&
    (
      item.is_digital_album === 1 ||
      item.is_digital_album === true ||
      item.is_digital_album === '1' ||
      item.shipping_enabled === false ||
      item.shipping_enabled === 0
    );

  // Use unitPrice if available (for wholesale/bundle), otherwise use regular price
  // Add validation to prevent NaN
  const unitPrice = getLineUnitPriceCents(item);
  const isWholesalePrice = item.isWholesalePrice || false;
  const wholesaleTier = item.wholesaleTier;
  const retailPrice = item.price_cents ?? item.price ?? 0;

  // Calculate savings (either wholesale or bundle discount)
  let itemSavings = 0;
  let savingsPercent = 0;

  if (isBundle && bundleDiscount > 0 && bundleOriginalPrice > 0) {
    // Bundle discount
    itemSavings = (bundleOriginalPrice - unitPrice) * item.quantity;
    savingsPercent = bundleDiscount;
  } else if (isWholesalePrice && retailPrice > 0) {
    // Wholesale discount
    itemSavings = (retailPrice - unitPrice) * item.quantity;
    savingsPercent = Math.round(((retailPrice - unitPrice) / retailPrice) * 100);
  }

  // Show price for paid items and checkout gift lines ($0)
  const hasValidPrice = isCheckoutPromo || unitPrice > 0;
  const lineTotalCents = unitPrice * item.quantity;
  const dualLineTotal = formatDualCurrency(lineTotalCents, customerCountry, currencyRates, customerCountryName);
  const showDual = showsReferenceCurrency(customerCountry, customerCountryName);

  return (
    <div className={`cart-item ${isWholesalePrice ? 'wholesale-item' : ''} ${isBundle ? 'bundle-item' : ''}`}>
      <div className="cart-item-image-container">
        <AsyncProductImage product={item} alt={item.name} className="cart-item-image" />
      </div>
      <div className="cart-item-details">
        <h4 className="cart-item-name">
          <span className="cart-item-name-text">{item.name}</span>
          {isCheckoutPromo && (
            <span className="checkout-promo-badge">
              {cart.freeGift}
            </span>
          )}
          {isDigitalOrVirtual && (
            <span
              className="digital-not-shipped-badge"
              title={cart.notShippedTitle}
              style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                color: '#fff',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '0.7rem',
                fontWeight: 600,
                marginLeft: '6px',
              }}
            >
              {cart.notShipped}
            </span>
          )}
          {isBundle && (
            <span className="bundle-badge">
              📦 {cart.bundle}
            </span>
          )}
          {isWholesalePrice && wholesaleTier && !isBundle && (
            <span className="wholesale-tier-badge">{wholesaleTier}</span>
          )}
        </h4>
        {(item.selectedSize || item.selectedColor) && (
          <div className="cart-item-variants">
            {item.selectedSize && <span>{cart.size} <strong>{item.selectedSize}</strong></span>}
            {item.selectedColor && <span>{cart.color} <strong>{item.selectedColor}</strong></span>}
          </div>
        )}
        {isBundle && bundleProducts.length > 0 && (
          <BundleContentsToggle
            bundleProducts={bundleProducts}
            listIdPrefix={`cart-bundle-${item.cartItemId || item.id}`}
          />
        )}
        {isBundle && itemSavings > 0 && (
          <div className="item-savings-inline bundle-savings">
            🎉 {fmt(cart.saveOff, { amount: formatCartMoney(itemSavings, customerCountry, { showCode: false }), pct: savingsPercent })}
          </div>
        )}
        {!isBundle && itemSavings > 0 && (
          <div className="item-savings-inline">
            {fmt(cart.saveOff, { amount: formatCartMoney(itemSavings, customerCountry, { showCode: false }), pct: savingsPercent })}
          </div>
        )}
      </div>
      <div className="cart-item-actions">
        {!isBundle && !isCheckoutPromo ? (
          <div className="quantity-controls">
            <button
              onClick={() => updateQuantity(item.cartItemId || item.id, Math.max(1, item.quantity - 1))}
              className="qty-btn"
              aria-label={cart.decreaseQty}
            >
              {getIcon('minus')}
            </button>
            <span className="quantity-display">{item.quantity}</span>
            <button
              onClick={() => updateQuantity(item.cartItemId || item.id, Math.min(50, item.quantity + 1))}
              className="qty-btn"
              aria-label={cart.increaseQty}
            >
              {getIcon('plus')}
            </button>
          </div>
        ) : (
          <div className="quantity-controls quantity-controls--fixed">
            <span className="quantity-display">{fmt(cart.qty, { n: item.quantity })}</span>
          </div>
        )}
        <div className="cart-item-remove-price">
          <button
            onClick={() => removeItem(item.cartItemId || item.id)}
            className="cart-item-remove"
            aria-label={isCheckoutPromo ? cart.removeGift : cart.removeItem}
            title={isCheckoutPromo ? cart.removeGiftFromCart : cart.removeFromCart}
          >
            {getIcon('delete')}
          </button>
          <div className="cart-item-total">
            {hasValidPrice ? (
              <div className="item-total-price">
                {showDual ? (
                  <>
                    {dualLineTotal?.cad}
                    <span className="item-ref-currency">{dualLineTotal?.secondary}</span>
                  </>
                ) : (
                  formatCartMoney(lineTotalCents, customerCountry, { showCode: false })
                )}
              </div>
            ) : (
              <div className="item-total-price price-unavailable">—</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem; 